import React from 'react';

/**
 * AssessmentStepper
 *
 * Props:
 * - step: current step number (1, 2, 2.5, 3, 4, 5)
 * - currentQuestionIndex: index of the current question (0-based)
 * - questions: array of question objects (for total length)
 * - progress: overall progress percentage (0-100)
 */
const AssessmentStepper = ({ step, currentQuestionIndex, questions, progress }) => {
  const totalQuestions = questions?.length || 0;

  return (
    <div className="mb-6">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200">
          Assessment Progress
        </h3>
        <span className="text-sm font-medium text-gray-600 dark:text-gray-400">
          {Math.round(progress)}% Complete
        </span>
      </div>

      {/* Progress Steps Visualization */}
      <div className="relative">
        {/* Progress Line */}
        <div className="absolute top-6 left-0 w-full h-1 bg-gray-200 dark:bg-gray-700 rounded-full">
          <div
            className="h-1 bg-emerald-500 dark:bg-emerald-400 rounded-full transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Progress Steps */}
        <div className="relative flex justify-between items-start">
          {[
            { key: 1, label: 'Company', active: step >= 1 },
            { key: 2, label: 'Industry', active: step >= 2 },
            { key: 2.5, label: 'Energy', active: step >= 2.5 },
            {
              key: 3,
              label: 'Assessment',
              active: step >= 3,
              subProgress:
                step === 3 && totalQuestions > 0
                  ? currentQuestionIndex / Math.max(totalQuestions, 1)
                  : 0
            },
            { key: 4, label: 'Review', active: step >= 4 },
            { key: 5, label: 'Results', active: step >= 5 }
          ].map((stepItem, index) => (
            <div key={stepItem.key} className="flex flex-col items-center">
              {/* Step Circle */}
              <div
                className={`
                  w-12 h-12 rounded-full border-2 flex items-center justify-center 
                  transition-all duration-300 relative
                  ${
                    stepItem.active
                      ? 'bg-emerald-500 dark:bg-emerald-400 border-emerald-500 dark:border-emerald-400 text-white'
                      : 'bg-white dark:bg-gray-700 border-gray-300 dark:border-gray-600 text-gray-400 dark:text-gray-500'
                  }
                  ${step === stepItem.key ? 'ring-4 ring-emerald-200 dark:ring-emerald-500/30 scale-110' : ''}
                `}
              >
                {stepItem.active ? (
                  step === stepItem.key ? (
                    <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
                  ) : (
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M5 13l4 4L19 7"
                      />
                    </svg>
                  )
                ) : (
                  <span className="text-sm font-medium">{index + 1}</span>
                )}

                {/* Sub-progress for Assessment Questions */}
                {stepItem.key === 3 && step === 3 && totalQuestions > 0 && (
                  <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2">
                    <div className="w-16 h-1 bg-gray-200 dark:bg-gray-700 rounded-full">
                      <div
                        className="h-1 bg-emerald-300 dark:bg-emerald-500 rounded-full transition-all duration-300"
                        style={{ width: `${(currentQuestionIndex / totalQuestions) * 100}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Step Label */}
              <span
                className={`
                  text-xs font-medium mt-2 transition-colors duration-300
                  ${
                    stepItem.active
                      ? 'text-emerald-600 dark:text-emerald-400'
                      : 'text-gray-500 dark:text-gray-400'
                  }
                  ${step === stepItem.key ? 'font-semibold' : ''}
                `}
              >
                {stepItem.label}
                {stepItem.key === 3 && step === 3 && totalQuestions > 0 && (
                  <div className="text-emerald-500 dark:text-emerald-400 text-xs mt-1">
                    {currentQuestionIndex + 1}/{totalQuestions}
                  </div>
                )}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Current Step Description */}
      <div className="text-center mt-4">
        <p className="text-sm text-gray-600 dark:text-gray-400 font-medium">
          {step === 1 && 'Please provide your facility information to get started'}
          {step === 2 && 'Select your industry specialization for targeted recommendations'}
          {step === 2.5 && 'Enter your energy usage and production data for accurate analysis'}
          {step === 3 && `Answer assessment questions to identify energy saving opportunities`}
          {step === 4 && 'Review your responses before submitting the assessment'}
          {step === 5 && 'Assessment completed! View your detailed energy savings report'}
        </p>
      </div>
    </div>
  );
};

export default AssessmentStepper;


